/**
 * The two things done *to* a member from the detail page that are not edits:
 * an amendment, and a print.
 *
 * An amendment is one of nine server-side actions; which ones this account
 * can take right now comes from the server too, so the picker never offers
 * what would be refused. Each action asks only for what it needs — a reason
 * from the configured list, a tier, a primary account — and answers with the
 * member as it now stands.
 */

import { useMutation, useQuery } from "@tanstack/react-query";
import { type FormEvent, useState } from "react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Modal } from "@/components/ui/dialog";
import { Field, Input } from "@/components/ui/field";
import { Select } from "@/components/ui/select";
import { api, errorMessage } from "@/services/api";
import { listAll } from "@/utils/geo";
import type { TranslationKey } from "@/constants/i18n";
import { TIER } from "@/pages/MembersPage";
import { useTranslation } from "@/store/locale";

export type Amendment =
  | "blacklist"
  | "cancellation"
  | "card-replacement"
  | "upgrade"
  | "downgrade"
  | "change-tier"
  | "activate"
  | "account-renew"
  | "change-primary-account";

const LABELS: Record<Amendment, TranslationKey> = {
  blacklist: "md.blacklist",
  cancellation: "md.cancellation",
  "card-replacement": "md.cardReplacement",
  upgrade: "md.upgrade",
  downgrade: "md.downgrade",
  "change-tier": "md.changeTier",
  activate: "md.activate",
  "account-renew": "md.accountRenew",
  "change-primary-account": "md.changePrimaryAccount",
};

/** Which configured reason list an action's reason comes from. */
const REASON_KIND: Partial<Record<Amendment, string>> = {
  blacklist: "blacklist",
  cancellation: "cancellation",
  "card-replacement": "card_replacement",
};
const NEEDS_TIER = new Set<Amendment>(["upgrade", "change-tier", "activate"]);
const NEEDS_PARENT = new Set<Amendment>(["downgrade", "change-primary-account"]);
const OTHERS = "__others__";

interface Reason {
  id: number;
  reason: string;
}

/** The actions this member can take right now, from the server. */
export function useAllowedAmendments(memberId: number) {
  return useQuery({
    queryKey: [`/members/${memberId}/`, "amendments"],
    queryFn: async ({ signal }) =>
      (await api.get<{ allowed: Amendment[] }>(`/members/${memberId}/amendments`, { signal }))
        .data.allowed,
  });
}

export function AmendmentDialog({
  memberId,
  action,
  currentTier,
  onClose,
  onDone,
}: {
  memberId: number;
  action: Amendment | null;
  currentTier: string | null;
  onClose: () => void;
  onDone: () => void;
}) {
  const t = useTranslation();
  const [reason, setReason] = useState("");
  const [others, setOthers] = useState("");
  const [tier, setTier] = useState(currentTier ?? "member");
  const [employeeNumber, setEmployeeNumber] = useState("");
  const [approvedBy, setApprovedBy] = useState("");
  const [parent, setParent] = useState("");
  const [error, setError] = useState<string | null>(null);

  const kind = action ? REASON_KIND[action] : undefined;
  const reasons = useQuery({
    queryKey: ["/reasons", kind],
    queryFn: ({ signal }) => listAll<Reason>("/reasons", { kind }, signal),
    enabled: !!kind,
    staleTime: 300_000,
  });
  const approvers = useQuery({
    queryKey: ["/reasons", "complimentary_approved_by"],
    queryFn: ({ signal }) =>
      listAll<Reason>("/reasons", { kind: "complimentary_approved_by" }, signal),
    enabled: !!action && NEEDS_TIER.has(action) && tier === "complimentary",
    staleTime: 300_000,
  });

  const apply = useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      api.post(`/members/${memberId}/amendments/${action}`, body),
    onSuccess: () => {
      toast.success(t("md.amendmentApplied"));
      onDone();
    },
    onError: (err) => setError(errorMessage(err, t("md.amendmentFailed"))),
  });

  if (!action) return null;

  const submit = (event: FormEvent) => {
    event.preventDefault();
    setError(null);
    if (kind) {
      const text = reason === OTHERS ? others.trim() : reason;
      if (!text) return setError(t("md.reasonRequired"));
      return apply.mutate({ reason: text });
    }
    if (NEEDS_TIER.has(action)) {
      if (tier === "employee" && !employeeNumber.trim())
        return setError(t("md.employeeNumberRequired"));
      return apply.mutate({
        membership_tier: tier,
        employee_number: employeeNumber.trim() || null,
        approved_by_id: approvedBy ? Number(approvedBy) : null,
      });
    }
    if (NEEDS_PARENT.has(action)) {
      if (!parent.trim()) return setError(t("md.primaryRequired"));
      return apply.mutate({ parent_account_number: parent.trim() });
    }
    // account-renew: ten minutes, nothing to ask.
    return apply.mutate({});
  };

  const formId = "amendment-form";
  return (
    <Modal
      open
      onClose={onClose}
      size="sm"
      title={t(LABELS[action])}
      description={t(`md.amend.${action}` as TranslationKey)}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            {t("common.cancel")}
          </Button>
          <Button type="submit" form={formId} loading={apply.isPending}>
            {t("md.apply")}
          </Button>
        </>
      }
    >
      <form id={formId} className="grid gap-4 pb-2" onSubmit={submit}>
        {kind ? (
          <>
            <Field label={t("hist.reason")} htmlFor="amendment-reason">
              <Select
                id="amendment-reason"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder={t("md.selectReason")}
              >
                {(reasons.data ?? []).map((r) => (
                  <option key={r.id} value={r.reason}>
                    {r.reason}
                  </option>
                ))}
                <option value={OTHERS}>{t("md.others")}</option>
              </Select>
            </Field>
            {reason === OTHERS ? (
              <Field label={t("md.specifyReason")} htmlFor="amendment-others">
                <Input
                  id="amendment-others"
                  autoFocus
                  value={others}
                  onChange={(e) => setOthers(e.target.value)}
                />
              </Field>
            ) : null}
          </>
        ) : null}
        {NEEDS_TIER.has(action) ? (
          <>
            <Field label={t("mem.membershipTier")} htmlFor="amendment-tier">
              <Select
                id="amendment-tier"
                value={tier}
                onChange={(e) => setTier(e.target.value)}
              >
                {TIER(t).map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </Select>
            </Field>
            {tier === "employee" ? (
              <Field label={t("mem.employeeNumber")} htmlFor="amendment-employee">
                <Input
                  id="amendment-employee"
                  value={employeeNumber}
                  onChange={(e) => setEmployeeNumber(e.target.value)}
                />
              </Field>
            ) : null}
            {tier === "complimentary" ? (
              <Field label={t("new.approvedBy")} htmlFor="amendment-approved">
                <Select
                  id="amendment-approved"
                  value={approvedBy}
                  onChange={(e) => setApprovedBy(e.target.value)}
                  placeholder={t("new.approvedBy")}
                >
                  {(approvers.data ?? []).map((r) => (
                    <option key={r.id} value={String(r.id)}>
                      {r.reason}
                    </option>
                  ))}
                </Select>
              </Field>
            ) : null}
          </>
        ) : null}
        {NEEDS_PARENT.has(action) ? (
          <Field label={t("md.primaryAccountNumber")} htmlFor="amendment-parent">
            <Input
              id="amendment-parent"
              autoFocus
              required
              value={parent}
              onChange={(e) => setParent(e.target.value)}
              placeholder={t("md.primaryAccountHint")}
            />
          </Field>
        ) : null}
        {error ? (
          <p role="alert" className="text-danger text-callout">
            {error}
          </p>
        ) : null}
      </form>
    </Modal>
  );
}

const PRINT_REASONS: [string, TranslationKey][] = [
  ["new", "md.printNew"],
  ["renew", "md.printRenew"],
  ["lost", "md.printLost"],
  ["damaged", "md.printDamaged"],
  ["activate", "md.activate"],
  ["upgrade", "md.upgrade"],
  ["downgrade", "md.downgrade"],
  ["others", "md.others"],
];

/**
 * Records the print on the server first — the pending job is marked printed,
 * the member leaves the queue — and only then opens the browser's print
 * dialog, so a card that is on paper is a card that is on record.
 */
export function PrintDialog({
  memberId,
  open,
  onClose,
  onPrinted,
}: {
  memberId: number;
  open: boolean;
  onClose: () => void;
  onPrinted: () => void;
}) {
  const t = useTranslation();
  const [reason, setReason] = useState("");
  const [others, setOthers] = useState("");
  const [error, setError] = useState<string | null>(null);
  const print = useMutation({
    mutationFn: () =>
      api.post(`/members/${memberId}/print`, {
        print_reason: reason || null,
        additional_reason: reason === "others" ? others.trim() : "",
      }),
    onSuccess: () => {
      onPrinted();
      onClose();
      window.print();
    },
    onError: (err) => setError(errorMessage(err, t("md.printFailed"))),
  });
  const formId = "print-form";
  return (
    <Modal
      open={open}
      onClose={onClose}
      size="sm"
      title={t("md.printCard")}
      description={t("md.printBody")}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            {t("common.cancel")}
          </Button>
          <Button type="submit" form={formId} loading={print.isPending}>
            {t("md.print")}
          </Button>
        </>
      }
    >
      <form
        id={formId}
        className="grid gap-4 pb-2"
        onSubmit={(event) => {
          event.preventDefault();
          setError(null);
          if (reason === "others" && !others.trim()) return setError(t("md.reasonRequired"));
          print.mutate();
        }}
      >
        <Field label={t("md.printReason")} htmlFor="print-reason">
          <Select
            id="print-reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder={t("md.printReasonHint")}
          >
            {PRINT_REASONS.map(([value, key]) => (
              <option key={value} value={value}>
                {t(key)}
              </option>
            ))}
          </Select>
        </Field>
        {reason === "others" ? (
          <Field label={t("md.specifyReason")} htmlFor="print-others">
            <Input
              id="print-others"
              value={others}
              onChange={(e) => setOthers(e.target.value)}
            />
          </Field>
        ) : null}
        {error ? (
          <p role="alert" className="text-danger text-callout">
            {error}
          </p>
        ) : null}
      </form>
    </Modal>
  );
}
